/**
 * Paginated subjects hook for Select components.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubjects } from '@/services/subjects.service';
import type { SelectOption } from '@/components/forms/Select';

/** Options for {@link useSubjectsSelect}. */
interface UseSubjectsSelectOptions {
  /** Filter by cycles. */
  cycles?: string[];
  /** Filter by statuses. */
  statuses?: string[];
  /** Filter core subjects only. */
  isCore?: boolean;
  /** Free-text search. */
  search?: string;
  /** Display order for in-person context. */
  displayOrder?: 'presentiel';
  /** Gate the query. */
  enabled?: boolean;
}

/**
 * Load subjects with infinite scroll support for Select components.
 * Accumulates pages as user scrolls.
 */
export const useSubjectsSelect = (options?: UseSubjectsSelectOptions) => {
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<SelectOption[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'subjects-select',
      options?.cycles,
      options?.statuses,
      options?.isCore,
      options?.search,
      options?.displayOrder,
      page,
    ],
    queryFn: () =>
      getSubjects({
        page,
        pageSize: 10,
        cycles: options?.cycles,
        statuses: options?.statuses,
        isCore: options?.isCore,
        search: options?.search,
        displayOrder: options?.displayOrder,
      }),
    enabled: options?.enabled !== false,
  });

  /** Merge new page data into accumulated list. */
  useMemo(() => {
    if (!data?.data) return;
    const newOptions = data.data.map((s) => ({ value: s.id, label: s.label }));

    if (page === 1) {
      setAccumulatedData(newOptions);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((o) => o.value));
        const uniqueNew = newOptions.filter((o) => !existingIds.has(o.value));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page]);

  /** Load next page. */
  const onLoadMore = useCallback(() => {
    if (data && page * 10 < data.total) {
      setPage((p) => p + 1);
    }
  }, [data, page]);

  /** Has more pages to load. */
  const hasMore = Boolean(data && page * 10 < data.total);

  return {
    options: accumulatedData,
    isLoading,
    onLoadMore,
    hasMore,
  };
};
